import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export const CONSENT_TEXT = {
  marketing_email: "J'accepte de recevoir par courriel des offres commerciales et des promotions de Ma Petite Ligue et de ses partenaires.",
  location_offers: "J'accepte l'utilisation facultative de ma ville déclarée pour recevoir des offres adaptées à ma région.",
  ad_measurement: "J'accepte les technologies facultatives de mesure ou de personnalisation publicitaire décrites dans les préférences de confidentialité.",
};

export function useGeo() {
  const [geo, setGeo] = useState(null);
  useEffect(() => { api.get("/geo").then(({ data }) => setGeo(data.countries)).catch(() => setGeo({})); }, []);
  return geo;
}

export function LocationFields({ value, onChange, geo, prefix = "loc" }) {
  const c = geo?.[value.country];
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <select className="mpl-input" value={value.country || ""} onChange={(e) => onChange({ country: e.target.value || null, region: null, city: "" })} aria-label="Pays" data-testid={`${prefix}-country`}>
        <option value="">Pays : non précisé</option>
        {Object.entries(geo || {}).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
      </select>
      <select className="mpl-input" disabled={!c} value={value.region || ""} onChange={(e) => onChange({ ...value, region: e.target.value || null })} aria-label="Région ou province" data-testid={`${prefix}-region`}>
        <option value="">Région : non précisée</option>
        {(c?.regions || []).map((r) => <option key={r}>{r}</option>)}
      </select>
      <input className="mpl-input" disabled={!c} list={`${prefix}-cities`} maxLength={60} placeholder="Ville (facultatif)" value={value.city || ""} onChange={(e) => onChange({ ...value, city: e.target.value })} aria-label="Ville" data-testid={`${prefix}-city`} />
      <datalist id={`${prefix}-cities`}>{(c?.cities || []).map((v) => <option key={v} value={v} />)}</datalist>
    </div>
  );
}

export default function PrivacySettings() {
  const geo = useGeo();
  const [p, setP] = useState(null);
  const [loc, setLoc] = useState({});
  useEffect(() => { api.get("/me/privacy").then(({ data }) => { setP(data); setLoc(data.location || {}); }); }, []);
  if (!p) return null;
  const save = async (body) => {
    try { const { data } = await api.put("/me/privacy", body); setP((o) => ({ ...o, ...data })); toast.success("Préférences enregistrées"); api.get("/me/privacy").then(({ data: d }) => setP(d)); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mpl-card space-y-4 p-5" data-testid="settings-privacy">
      <p className="flex items-center gap-2 text-sm font-bold"><ShieldCheck size={15} className="text-[#B7F52A]" /> Confidentialité et offres partenaires</p>
      <p className="text-xs text-slate-400">Conditions acceptées : version {p.terms.version || "antérieure"}{p.terms.accepted_at && ` le ${fmtDateTime(p.terms.accepted_at)}`}. <a href="/conditions" className="underline">Conditions</a> · <a href="/confidentialite" className="underline">Confidentialité</a></p>
      <div className="space-y-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300"><MapPin size={13} /> Ma localisation (déclarée par moi, jamais par GPS)</p>
        <LocationFields value={loc} onChange={setLoc} geo={geo} prefix="privacy" />
        <button type="button" onClick={() => save({ location: { country: loc.country || null, region: loc.region || null, city: loc.city || null } })} className="mpl-btn-ghost text-xs" data-testid="privacy-location-save">Enregistrer ma localisation</button>
      </div>
      <div className="space-y-2">
        {Object.entries(p.consents).map(([k, c]) => (
          <label key={k} className="flex items-start gap-3 text-xs text-slate-300">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#B7F52A]" checked={c.granted} onChange={(e) => save({ consents: { [k]: e.target.checked } })} data-testid={`privacy-consent-${k}`} />
            <span>{CONSENT_TEXT[k]}{c.at && <span className="block text-[10px] text-slate-500">Mis à jour le {fmtDateTime(c.at)}</span>}</span>
          </label>
        ))}
        <p className="text-[11px] text-slate-500">Ces choix sont facultatifs et modifiables à tout moment : refuser ne change rien au jeu. Les offres partenaires restent identifiées comme telles.</p>
      </div>
      {p.history?.length > 0 && (
        <details className="text-xs text-slate-400" data-testid="privacy-history"><summary className="cursor-pointer">Historique de mes choix</summary>
          <ul className="mt-2 space-y-1">{p.history.map((h, i) => <li key={i}>{fmtDateTime(h.created_at)} · {h.purpose} : {h.granted ? "accepté" : "refusé"} ({h.context}, v{h.version})</li>)}</ul>
        </details>
      )}
      <p className="text-[11px] text-slate-500">Accès, rectification ou suppression de tes données : <a href="mailto:hello@mapetiteligue.com" className="underline">hello@mapetiteligue.com</a> ou « Supprimer mon compte » plus bas.</p>
    </section>
  );
}
